import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getDiagnostics, createDiagnostic } from '../services/api';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.png';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [projectName, setProjectName] = useState('');

  const { data: diagnostics } = useQuery({
    queryKey: ['diagnostics'],
    queryFn: getDiagnostics,
  });

  const createMutation = useMutation({
    mutationFn: createDiagnostic,
    onSuccess: (data) => {
      setShowModal(false);
      navigate(`/questionnaire/${data.id}`);
    },
  });

  const latestCompleted = diagnostics?.find(
    (d) => d.status === 'completed' || d.status === 'report_generated'
  );

  const getStatusStyle = (status) => {
    switch (status) {
      case 'completed':
      case 'report_generated':
        return 'bg-green-900/30 text-green-400 border border-green-700/50';
      case 'in_progress':
        return 'bg-blue-900/30 text-blue-300 border border-blue-700/50';
      default:
        return 'bg-neutral-800 text-neutral-400 border border-neutral-700';
    }
  };

  return (
    <div className="min-h-screen bg-[#05070d] text-white relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      {/* Decorative glow */}
      <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-blue-300/10 rounded-full blur-3xl pointer-events-none"></div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-[#05070d]/95 backdrop-blur-md border-b border-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center gap-3">
              <img
                src={logo}
                alt="RAL Real Advisor"
                className="w-10 h-10 object-contain"
              />
              <div className="flex items-center">
                <span className="text-2xl font-serif font-bold text-white">
                  RAL
                </span>
                <span className="text-2xl font-serif font-light text-[#9fc6ff] ml-1">
                  Real Advisor
                </span>
              </div>
            </Link>

            <div className="flex items-center gap-6">
              <span className="text-neutral-400 text-sm hidden sm:block">
                {user?.email}
              </span>
              <button
                onClick={logout}
                className="text-[#9fc6ff] hover:text-white text-sm font-medium transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12 relative">
        {/* Welcome Section */}
        <div className="mb-12">
          <span className="text-[#9fc6ff] text-sm font-medium tracking-[0.3em] uppercase mb-2 block">
            Dashboard
          </span>
          <h1 className="text-4xl font-serif font-bold text-white">
            Welcome back, {user?.company_name || 'Team'}
          </h1>
        </div>

        {/* Latest Score Card */}
        {latestCompleted && (
          <div className="bg-white/5 backdrop-blur border border-white/10 p-8 mb-8">
            <div className="flex justify-between items-center mb-6">
              <span className="text-neutral-400 text-sm tracking-wide uppercase">
                Latest Diagnostic Score
              </span>
              <span className="text-[#9fc6ff] text-sm font-medium">
                {latestCompleted.project_name || 'General Assessment'}
              </span>
            </div>
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="text-7xl font-serif font-bold text-white mb-2">
                  {latestCompleted.overall_score}
                  <span className="text-2xl text-neutral-500">/100</span>
                </div>
                <div className="text-[#9fc6ff] font-medium text-lg">
                  {latestCompleted.maturity_level}
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => navigate(`/report/${latestCompleted.id}`)}
                  className="px-8 py-4 bg-[#9fc6ff] text-[#05070d] font-medium tracking-wide uppercase hover:opacity-90 transition-all"
                >
                  View Full Report
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Diagnostics Table */}
        <div className="bg-white/5 backdrop-blur border border-white/10">
          <div className="p-6 border-b border-white/10 flex justify-between items-center">
            <h2 className="text-xl font-serif font-bold text-white">
              Your Diagnostics
            </h2>
            <button
              onClick={() => setShowModal(true)}
              className="px-6 py-3 bg-[#9fc6ff] text-[#05070d] font-medium tracking-wide uppercase hover:opacity-90 transition-all text-sm"
            >
              Start New
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-black/20">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider">
                    Project
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {diagnostics?.length === 0 && (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-6 py-12 text-center text-neutral-500"
                    >
                      No diagnostics yet. Start your first assessment to get insights.
                    </td>
                  </tr>
                )}

                {diagnostics?.map((d) => (
                  <tr key={d.id} className="hover:bg-white/[0.03] transition-colors">
                    <td className="px-6 py-4 text-white">
                      {d.project_name || 'Untitled'}
                    </td>
                    <td className="px-6 py-4 text-neutral-400">
                      {new Date(d.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-3 py-1 text-xs rounded-sm ${getStatusStyle(
                          d.status
                        )}`}
                      >
                        {d.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-white font-medium">
                      {d.overall_score ? `${d.overall_score}/100` : '—'}
                    </td>
                    <td className="px-6 py-4">
                      {d.status === 'in_progress' || d.status === 'pending' ? (
                        <button
                          onClick={() => navigate(`/questionnaire/${d.id}`)}
                          className="text-[#9fc6ff] hover:text-white font-medium transition-colors"
                        >
                          Continue →
                        </button>
                      ) : (
                        <button
                          onClick={() => navigate(`/report/${d.id}`)}
                          className="text-[#9fc6ff] hover:text-white font-medium transition-colors"
                        >
                          View Report →
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[60]">
          <div className="bg-[#0b1220] border border-white/10 p-8 max-w-md w-full mx-4">
            <h3 className="text-2xl font-serif font-bold text-white mb-2">
              Start New Diagnostic
            </h3>
            <p className="text-neutral-400 mb-6">
              Enter a project name to help identify this assessment.
            </p>

            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              className="w-full px-4 py-3 bg-[#05070d] border border-white/10 text-white placeholder-neutral-500 focus:outline-none focus:border-[#9fc6ff] transition-colors mb-6"
              placeholder="Project Name (optional)"
            />

            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-3 border border-white/10 text-neutral-300 hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => createMutation.mutate(projectName)}
                className="px-6 py-3 bg-[#9fc6ff] text-[#05070d] font-medium hover:opacity-90 transition-all"
              >
                Begin Assessment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;